/**
 * Chat defaults — "Disappearing messages for new chats" (phone parity, FR-MSG-004).
 * The default is stored locally only; each chat keeps its own timer in meta
 * `disappear.<conversationId>` (the per-chat source of truth). Existing chats are never touched.
 */
import { DISAPPEAR_OPTIONS, type DisappearTimerSec } from "@veto/domain";
import { getMeta, setMeta } from "@veto/storage";

const KEY = "veto.chats.defaultDisappear";

const VALID = new Set<number>(DISAPPEAR_OPTIONS.map((o) => o.sec));

export function loadDefaultDisappear(): DisappearTimerSec {
  try {
    const n = Number(localStorage.getItem(KEY) ?? 0);
    return (VALID.has(n) ? n : 0) as DisappearTimerSec;
  } catch {
    return 0;
  }
}

export function saveDefaultDisappear(sec: DisappearTimerSec): void {
  try {
    if (sec) localStorage.setItem(KEY, String(sec));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode — session-only */
  }
}

export function disappearLabel(sec: number): string {
  return DISAPPEAR_OPTIONS.find((o) => o.sec === sec)?.label ?? `${sec} seconds`;
}

/** "Off for new chats" / "1 hour for new chats". */
export function defaultDisappearSummary(sec: number = loadDefaultDisappear()): string {
  return `${sec ? disappearLabel(sec) : "Off"} for new chats`;
}

/**
 * A direct conversation was just created: give it the default timer, unless it already has
 * a setting of its own. Returns the timer the chat now has.
 */
export async function applyDefaultDisappear(conversationId: string): Promise<DisappearTimerSec> {
  const existing = await getMeta(`disappear.${conversationId}`);
  if (existing !== undefined && existing !== "")
    return (Number(existing) || 0) as DisappearTimerSec;
  const sec = loadDefaultDisappear();
  if (sec) await setMeta(`disappear.${conversationId}`, String(sec));
  return sec;
}
